import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import useVoyagerStore from '../../stores/useVoyagerStore';
import type { LandingAgentTurn } from '../../stores/slices/landingAgentSlice';
import type { LadderAnswer } from '../Ladder/claims';

/** The routes an answer can name (#760), as the route line shows them. */
export const ROUTE_LABELS: Record<string, string> = {
  catalog: 'Catalog', why: 'Why', data: 'Data', knowledge: 'Knowledge', document: 'Document', help: 'Help',
  decline: 'Outside scope', prior_work: 'Prior work',
};
// Change re-asks the same words, so Document — which needs an attachment — is never offered.
const CHANGE_TO = ['catalog', 'why', 'data', 'knowledge', 'prior_work', 'help', 'decline'];

/**
 * The route line on an answer (#760): a chip per route the router decided, its reason, and Change — a menu of
 * the other routes that re-asks the same question under the one picked (logged as `route_changed`). The re-ask is
 * the turn's own request as it went on the wire (`askedRequest`), only the route changed; a turn without one (an
 * answer from before #760) offers no Change rather than guess its scope.
 */
/** The route an answer was decided under and its Change (the route line's, and the answer's More menu's). */
export function useRouteChange(turn: LandingAgentTurn) {
  const route = (turn.result?.ladder as LadderAnswer | null | undefined)?.route;
  const send = useVoyagerStore((s) => s.sendLandingAgentQuery);
  if (!route || !route.routes?.length) return null;
  const reask = turn.askedRequest;
  return {
    route,
    label: route.routes.map((r) => ROUTE_LABELS[r] || r).join(' · '),
    /** the other routes a Change offers; none without the turn's own request */
    choices: reask ? CHANGE_TO.filter((r) => r !== route.route).map((r) => ({ to: r, label: `Ask as ${ROUTE_LABELS[r]}` })) : [],
    change: (to: string) => {
      // #1807: the Change names the turn it replaces; the turn is replaced in place only once the server says so
      if (reask) send(reask.query, { reask, route: to, routeFrom: route.route, replaces: turn.sessionId || null });
    },
  };
}

export default function RouteChip({ turn }: { turn: LandingAgentTurn }) {
  const routing = useRouteChange(turn);
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  if (!routing) return null;
  const { route } = routing;
  const reask = turn.askedRequest;
  const change = (to: string) => {
    setAnchor(null);
    routing.change(to);
  };
  return (
    <Box data-testid="route-line" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
      {route.routes.map((r) => (
        <Chip key={r} size="small" label={ROUTE_LABELS[r] || r} data-testid={`route-chip-${r}`}
              variant="outlined"
              sx={{ borderRadius: 99, borderColor: 'var(--accent)', color: 'var(--accent)', bgcolor: 'var(--accent-soft)', fontWeight: 600 }} />
      ))}
      <Typography variant="caption" data-testid="route-reason"
                  sx={{ fontStyle: 'italic', color: 'var(--ink-3)', minWidth: 0, overflowWrap: 'anywhere' }}>
        {route.reason}
      </Typography>
      {reask && <Button size="small" variant="text" aria-haspopup="menu" onClick={(e) => setAnchor(e.currentTarget)}
              sx={{ minWidth: 0, px: 0.75, textTransform: 'none', color: 'var(--accent)' }}>
        Change
      </Button>}
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {routing.choices.map((c) => (
          <MenuItem key={c.to} onClick={() => change(c.to)}>{c.label}</MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
